import { useState, useCallback } from 'react';
import {
  FileSearchQuery,
  FileSearchResults,
  FileSearchState,
  DEFAULT_FILE_SEARCH_QUERY,
  validateSearchQuery,
  FileStatus,
  FileAuditDateRange,
  FileSortField,
  SortOrder,
  PaginationConfig,
} from '../types/fileAudit';
import { fileAuditApi } from '../services/api/fileAudit';

export interface UseFileSearchOptions {
  /** Initial query state */
  initialQuery?: Partial<FileSearchQuery>;
  /** Callback when search completes successfully */
  onSearchComplete?: (results: FileSearchResults) => void;
  /** Callback when search fails */
  onSearchError?: (error: string) => void;
}

export interface UseFileSearchReturn {
  /** Current search query */
  query: FileSearchQuery;
  /** Search results */
  results: FileSearchResults | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Execute search with current query */
  search: (override?: Partial<FileSearchQuery>) => Promise<void>;
  /** Update query parameters */
  updateQuery: (updates: Partial<FileSearchQuery>) => void;
  /** Reset query to defaults */
  resetQuery: () => void;
  /** Load more results (cursor-based pagination) */
  loadMore: () => Promise<void>;
  /** Whether there are more results to load */
  hasMore: boolean;
  /** Validation errors for current query */
  validationErrors: string[];
}

/**
 * Create a default file search query with optional overrides
 */
function createDefaultQuery(
  overrides?: Partial<FileSearchQuery>
): FileSearchQuery {
  return {
    ...DEFAULT_FILE_SEARCH_QUERY,
    ...overrides,
    pagination: {
      ...DEFAULT_FILE_SEARCH_QUERY.pagination,
      ...overrides?.pagination,
    },
  };
}

/**
 * Hook for file audit search API integration
 * Provides search state management, validation, and cursor-based pagination
 */
export function useFileSearch(
  options: UseFileSearchOptions = {}
): UseFileSearchReturn {
  const { initialQuery, onSearchComplete, onSearchError } = options;

  // Initialize state
  const [state, setState] = useState<FileSearchState>({
    query: createDefaultQuery(initialQuery),
    results: null,
    isLoading: false,
    error: null,
  });

  // Validate current query
  const validationErrors = validateSearchQuery(state.query);

  /**
   * Execute search.
   *
   * THUB-130: accept an optional query override. The previous caller did
   * `updateQuery(...)` then `setTimeout(() => search(), 0)`, but `search` was
   * memoized over the OLD `state.query` (and updateQuery's setState hadn't
   * committed yet), so the first search after picking a filter fired with the
   * stale/empty query — the backend then took the no-index scan branch and
   * returned arbitrary rows regardless of the selected filters. Passing the
   * freshly-built query in sidesteps the stale closure entirely.
   */
  const search = useCallback(async (override?: Partial<FileSearchQuery>): Promise<void> => {
    const effectiveQuery: FileSearchQuery = override
      ? { ...state.query, ...override }
      : state.query;

    // Validate before searching
    const errors = validateSearchQuery(effectiveQuery);
    if (errors.length > 0) {
      const errorMessage = errors.join('; ');
      setState((prev) => ({
        ...prev,
        error: errorMessage,
        isLoading: false,
      }));
      onSearchError?.(errorMessage);
      return;
    }

    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      // Convert FileSearchQuery to FileEventSearchParams for the API
      const result = await fileAuditApi.searchFileEvents({
        filename: effectiveQuery.filename,
        partnerId: effectiveQuery.partnerId,
        taskId: effectiveQuery.taskId,
        startDate: effectiveQuery.dateRange?.start,
        endDate: effectiveQuery.dateRange?.end,
        limit: effectiveQuery.pagination.pageSize,
        cursor: undefined, // Fresh search, no cursor
        // THUB-244: Transfer History lists FILES, not raw events. Must match the loadMore call
        // below, or page 2 would arrive as events while page 1 was files.
        groupBy: 'file',
      });

      const searchResults: FileSearchResults = {
        items: result.events,
        totalCount: result.totalCount ?? result.events.length,
        hasMore: !!result.nextCursor,
        nextCursor: result.nextCursor,
      };

      setState((prev) => ({
        ...prev,
        results: searchResults,
        isLoading: false,
        error: null,
      }));

      onSearchComplete?.(searchResults);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An unexpected error occurred';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      onSearchError?.(errorMessage);
    }
  }, [state.query, onSearchComplete, onSearchError]);

  /**
   * Update query parameters (merges with existing query)
   */
  const updateQuery = useCallback(
    (updates: Partial<FileSearchQuery>): void => {
      setState((prev) => ({
        ...prev,
        query: {
          ...prev.query,
          ...updates,
          pagination: {
            ...prev.query.pagination,
            ...updates.pagination,
            // Reset to page 1 when filters change (unless pagination is explicitly updated)
            page: updates.pagination?.page ?? 1,
          },
        },
        // Clear previous results when query changes
        results: null,
        error: null,
      }));
    },
    []
  );

  /**
   * Reset query to default state
   */
  const resetQuery = useCallback((): void => {
    setState({
      query: createDefaultQuery(initialQuery),
      results: null,
      isLoading: false,
      error: null,
    });
  }, [initialQuery]);

  /**
   * Load more results using cursor-based pagination
   */
  const loadMore = useCallback(async (): Promise<void> => {
    if (!state.results?.nextCursor || state.isLoading) {
      return;
    }

    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const result = await fileAuditApi.searchFileEvents({
        filename: state.query.filename,
        partnerId: state.query.partnerId,
        taskId: state.query.taskId,
        startDate: state.query.dateRange?.start,
        endDate: state.query.dateRange?.end,
        limit: state.query.pagination.pageSize,
        cursor: state.results.nextCursor,
        groupBy: 'file',  // THUB-244: must match the initial search
      });

      setState((prev) => {
        const existingItems = prev.results?.items ?? [];
        const newResults: FileSearchResults = {
          items: [...existingItems, ...result.events],
          totalCount: result.totalCount ?? existingItems.length + result.events.length,
          hasMore: !!result.nextCursor,
          nextCursor: result.nextCursor,
        };

        return {
          ...prev,
          results: newResults,
          isLoading: false,
          error: null,
          query: {
            ...prev.query,
            pagination: {
              ...prev.query.pagination,
              page: prev.query.pagination.page + 1,
              cursor: result.nextCursor,
            },
          },
        };
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load more results';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      onSearchError?.(errorMessage);
    }
    // THUB-298: depend on `state.results`, not `state.results?.nextCursor`.
    //
    // The React Compiler infers `state.results` (it cannot narrow through the optional chain), so
    // the manual dep list was *more* specific than the inferred one and the memoization could not
    // be preserved -- meaning this hook was skipped for optimization entirely. Widening the dep
    // to the object matches the inference. loadMore is only handed to a "Load more" button, so a
    // slightly more frequent identity change costs nothing, and it removes the chance of calling
    // with a nextCursor that has since moved on.
  }, [state.results, state.query, state.isLoading, onSearchError]);

  return {
    query: state.query,
    results: state.results,
    isLoading: state.isLoading,
    error: state.error,
    search,
    updateQuery,
    resetQuery,
    loadMore,
    hasMore: state.results?.hasMore ?? false,
    validationErrors,
  };
}

// Re-export types for convenience
export type {
  FileSearchQuery,
  FileSearchResults,
  FileSearchState,
  FileStatus,
  FileAuditDateRange,
  FileSortField,
  SortOrder,
  PaginationConfig,
};
